import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useSearchParams } from 'react-router-dom';
import { JournalFlipbook, JournalCountdown } from '../components/JournalFlipbook';
import { JournalActions } from '../components/JournalActions';
import { BookOpen, ExternalLink, Award, Crown, Star } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '../components/ui/dialog';
import { ASSETS, CONFERENCE } from '../mock';

const BASE = process.env.REACT_APP_BACKEND_URL;
const TIER_ORDER = CONFERENCE.sponsorships.map((s) => s.tier);
const AD_ORDER = CONFERENCE.journal.map((j) => j.type);
const AD_COLS = { 'Full Page Ad': 'sm:grid-cols-2', 'Half Page Ad': 'sm:grid-cols-2 lg:grid-cols-3', 'Quarter Page Ad': 'sm:grid-cols-3 lg:grid-cols-4', 'Business Card Ad': 'sm:grid-cols-3 lg:grid-cols-5' };

const groupBy = (items, order) => {
  const levels = [...order, ...new Set(items.map((i) => i.level).filter((l) => !order.includes(l)))];
  return levels.map((level) => ({ level, items: items.filter((i) => i.level === level) })).filter((g) => g.items.length);
};

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-');

function AdCard({ item, onOpen }) {
  return (
    <figure className="card-dark rounded-md overflow-hidden flex flex-col group" data-testid={`journal-item-${item.id}`}>
      <button type="button" onClick={() => onOpen(item)} className="block bg-white/5 overflow-hidden" aria-label={`Enlarge ad from ${item.name}`} data-testid={`journal-item-open-${item.id}`}>
        <img src={`${BASE}${item.src}`} alt={`${item.name} ${item.level}`} loading="lazy" className="w-full h-auto block transition-transform duration-500 group-hover:scale-[1.02]" />
      </button>
      <figcaption className="px-4 py-3 border-t border-gold/20 flex items-center justify-between gap-2">
        <span className="text-cbtu-cream text-sm font-medium">{item.name}</span>
        {item.link && (
          <a href={item.link} target="_blank" rel="noopener noreferrer" className="text-gold hover:text-gold-bright text-xs inline-flex items-center gap-1" data-testid={`journal-item-link-${item.id}`}>
            Visit <ExternalLink size={11} />
          </a>
        )}
      </figcaption>
    </figure>
  );
}

function Group({ title, icon: Icon, groups, onOpen, colsFor }) {
  if (!groups.length) return null;
  return (
    <section className="mb-16">
      <h2 className="font-display text-3xl md:text-4xl text-cbtu-cream mb-8 flex items-center gap-3"><Icon className="text-gold" size={26} /> {title}</h2>
      {groups.map((g) => (
        <div key={g.level} className="mb-10" data-testid={`journal-group-${slug(g.level)}`}>
          <div className="flex items-center gap-3 mb-4">
            <span className="font-heading tracking-widest text-gold text-xs">{g.level.toUpperCase()}</span>
            <span className="h-px flex-1 bg-gold/20" />
          </div>
          <div className={`grid gap-5 ${colsFor(g.level)}`}>
            {g.items.map((it) => <AdCard key={it.id} item={it} onOpen={onOpen} />)}
          </div>
        </div>
      ))}
    </section>
  );
}

function FlipbookSection() {
  const [params] = useSearchParams();
  const adminKey = params.get('preview') ? sessionStorage.getItem('cbtu_admin_pw') : null;
  const [book, setBook] = useState(null);
  const load = useCallback(() => {
    axios.get(`${BASE}/api/journal-book`, { params: adminKey ? { key: adminKey } : {} }).then((r) => setBook(r.data)).catch(() => setBook(null));
  }, [adminKey]);
  useEffect(() => { load(); }, [load]);
  if (!book) return null;
  const releaseLabel = new Date(book.release_at).toLocaleString('en-US', { weekday: 'long', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' });
  return (
    <section className="mb-16" id="flipbook" data-testid="journal-flipbook-section">
      <h2 className="font-display text-3xl md:text-4xl text-cbtu-cream mb-2 flex items-center gap-3"><BookOpen className="text-gold" size={26} /> 2026 Digital Ad Journal</h2>
      {book.preview && <div className="mb-4 inline-block bg-orange-500/15 border border-orange-400/60 text-orange-200 text-xs font-heading tracking-widest px-3 py-1.5 rounded-sm" data-testid="journal-admin-preview-badge">ADMIN PREVIEW · THE PUBLIC CAN'T SEE THIS UNTIL {releaseLabel.toUpperCase()} ET</div>}
      {book.pages.length > 0 ? (
        <>
          <JournalFlipbook pages={book.pages} keyParam={book.preview ? adminKey : null} />
          <JournalActions keyParam={book.preview ? adminKey : null} />
        </>
      ) : (
        <div className="card-dark rounded-md p-8 md:p-10 grid md:grid-cols-[auto_1fr] gap-8 items-center" data-testid="journal-locked">
          <img src={ASSETS.digitalJournalCover} alt="2026 Digital Ad Journal cover" className="w-48 rounded-md gold-border mx-auto" />
          <div>
            <div className="chip mb-3 inline-flex">COMING SOON</div>
            <h3 className="font-display text-3xl text-cbtu-cream">The {book.page_count}-page journal opens <span className="gold-text-fill">{releaseLabel} ET</span></h3>
            <p className="text-cbtu-text/85 mt-2 mb-5">Come back right here to flip through every page, honoring our Champions of Labor and thanking our sponsors and advertisers.</p>
            <JournalCountdown releaseAt={book.release_at} onRelease={load} />
          </div>
        </div>
      )}
    </section>
  );
}

export default function DigitalJournal() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    axios.get(`${BASE}/api/digital-journal`).then((r) => setItems(r.data.items || [])).catch(() => setItems([])).finally(() => setLoading(false));
  }, []);

  const sponsors = groupBy(items.filter((i) => i.kind === 'sponsor'), TIER_ORDER);
  const ads = groupBy(items.filter((i) => i.kind === 'ad'), AD_ORDER);

  return (
    <div className="fade-in-up">
      <section className="banner-bg section-noise">
        <div className="max-w-7xl mx-auto px-4 py-14 grid md:grid-cols-[1fr_auto] gap-10 items-center">
          <div>
          <div className="chip mb-3 inline-flex items-center gap-2"><BookOpen size={13} /> 2026 CONFERENCE</div>
          <h1 className="font-display text-5xl md:text-6xl text-cbtu-cream">DIGITAL AD <span className="gold-text-fill">JOURNAL</span></h1>
          <p className="text-cbtu-text/85 max-w-2xl mt-4">Thank you to the sponsors and advertisers supporting the CBTU Region III 2026 Regional Conference. Tap any ad to view it full size.</p>
          {!CONFERENCE.recap.active && <Link to="/conference#sponsor" className="gold-button mt-6 px-6 py-3 rounded-sm inline-flex items-center gap-2" data-testid="journal-become-sponsor-btn"><Award size={16} /> Become a Sponsor</Link>}
          </div>
          <img src={ASSETS.digitalJournalCover} alt="CBTU Region III 2026 Digital Ad Journal cover" className="w-full max-w-xs md:w-72 mx-auto rounded-md gold-border shadow-2xl" data-testid="journal-cover-image" />
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 py-14">
        <FlipbookSection />
        {loading && <div className="text-cbtu-muted" data-testid="journal-loading">Loading journal…</div>}

        <Group title="Our Sponsors" icon={Crown} groups={sponsors} onOpen={setOpen} colsFor={(l) => (l === TIER_ORDER[0] ? 'sm:grid-cols-2' : 'sm:grid-cols-2 lg:grid-cols-3')} />
        <Group title="Advertisements" icon={Star} groups={ads} onOpen={setOpen} colsFor={(l) => AD_COLS[l] || 'sm:grid-cols-2 lg:grid-cols-3'} />
      </div>

      <Dialog open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
        <DialogContent className="max-w-4xl bg-cbtu-black border-gold/40 p-3" data-testid="journal-lightbox">
          <DialogTitle className="text-cbtu-cream font-heading tracking-widest text-sm px-1">{open?.name} · {open?.level}</DialogTitle>
          {open && <img src={`${BASE}${open.src}`} alt={`${open.name} ${open.level}`} className="w-full max-h-[80vh] object-contain" />}
          {open?.link && (
            <a href={open.link} target="_blank" rel="noopener noreferrer" className="text-gold text-sm inline-flex items-center gap-1 px-1" data-testid="journal-lightbox-link">Visit {open.name} <ExternalLink size={12} /></a>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
